import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useToast } from '../hooks/use-toast';
import {
  ArrowLeft, Plus, X, Search, AlertTriangle, Clock, CheckCircle2,
  ListTodo, ChevronRight, Tag, PhoneCall, Pencil, Trash2,
  CircleDot, MessageSquare, ExternalLink
} from 'lucide-react';
import axios from 'axios';
import Logo from './Logo';

import { BACKEND_URL } from "../lib/backendUrl";
const API = `${BACKEND_URL}/api`;

const STATUS_CONFIG = {
  pending: { label: 'Pending', icon: Clock, cls: 'bg-gray-500/15 text-gray-400 border-gray-500/30' },
  todo: { label: 'To Do', icon: ListTodo, cls: 'bg-blue-500/15 text-blue-400 border-blue-500/30' },
  in_progress: { label: 'In Progress', icon: CircleDot, cls: 'bg-amber-500/15 text-amber-400 border-amber-500/30' },
  resolved: { label: 'Resolved', icon: CheckCircle2, cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30' },
};

const PRIORITY_CONFIG = {
  low: { label: 'Low', cls: 'bg-gray-500/15 text-gray-400' },
  medium: { label: 'Medium', cls: 'bg-amber-500/15 text-amber-400' },
  high: { label: 'High', cls: 'bg-red-500/15 text-red-400' },
};

const Tickets = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newNote, setNewNote] = useState('');

  const [form, setForm] = useState({
    title: '', description: '', priority: 'medium',
    contact_name: '', contact_phone: '', tags: [],
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchTickets = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status_filter = statusFilter;
      if (priorityFilter !== 'all') params.priority_filter = priorityFilter;
      if (searchQuery) params.search = searchQuery;
      const res = await axios.get(`${API}/tickets/`, { headers, params });
      setTickets(res.data);
    } catch { /* */ } finally { setLoading(false); }
  }, [token, statusFilter, priorityFilter, searchQuery]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/tickets/stats`, { headers });
      setStats(res.data);
    } catch { /* */ }
  }, [token]);

  useEffect(() => { fetchTickets(); fetchStats(); }, [fetchTickets, fetchStats]);

  const handleCreate = async () => {
    if (!form.title.trim()) {
      toast({ title: 'Title is required', variant: 'destructive' });
      return;
    }
    try {
      await axios.post(`${API}/tickets/`, form, { headers });
      toast({ title: 'Ticket created' });
      setShowCreateModal(false);
      setForm({ title: '', description: '', priority: 'medium', contact_name: '', contact_phone: '', tags: [] });
      fetchTickets();
      fetchStats();
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail, variant: 'destructive' });
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      const res = await axios.put(`${API}/tickets/${ticketId}`, { status: newStatus }, { headers });
      toast({ title: `Status updated to ${newStatus.replace('_', ' ')}` });
      if (selectedTicket?.id === ticketId) setSelectedTicket(res.data);
      fetchTickets();
      fetchStats();
    } catch { /* */ }
  };

  const handlePriorityChange = async (ticketId, newPriority) => {
    try {
      const res = await axios.put(`${API}/tickets/${ticketId}`, { priority: newPriority }, { headers });
      if (selectedTicket?.id === ticketId) setSelectedTicket(res.data);
      fetchTickets();
    } catch { /* */ }
  };

  const handleDelete = async (ticketId) => {
    try {
      await axios.delete(`${API}/tickets/${ticketId}`, { headers });
      toast({ title: 'Ticket deleted' });
      setSelectedTicket(null);
      setView('list');
      fetchTickets();
      fetchStats();
    } catch { /* */ }
  };

  const handleAddNote = async (ticketId) => {
    if (!newNote.trim()) return;
    try {
      const res = await axios.post(`${API}/tickets/${ticketId}/notes`, { text: newNote }, { headers });
      setSelectedTicket(res.data);
      setNewNote('');
      toast({ title: 'Note added' });
    } catch { /* */ }
  };

  const openDetail = async (ticket) => {
    try {
      const res = await axios.get(`${API}/tickets/${ticket.id}`, { headers });
      setSelectedTicket(res.data);
      setView('detail');
    } catch { /* */ }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a1628] via-[#0d1f35] to-[#0a1628]" data-testid="tickets-page">
      <header className="border-b border-white/5 bg-[#0a1628]/80 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo linkTo="/dashboard" />
            <div className="h-6 w-px bg-white/10" />
            {view !== 'list' && (
              <Button variant="ghost" onClick={() => { setView('list'); setSelectedTicket(null); }} className="text-gray-400 hover:text-white" data-testid="tickets-back-btn">
                <ArrowLeft className="w-4 h-4 mr-2" /> Tickets
              </Button>
            )}
            <h1 className="text-xl font-bold text-white">Support Tickets</h1>
          </div>
          {view === 'list' && (
            <Button onClick={() => setShowCreateModal(true)} className="bg-purple-600 hover:bg-purple-700 text-white" data-testid="create-ticket-btn">
              <Plus className="w-4 h-4 mr-2" /> New Ticket
            </Button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        {view === 'list' && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6" data-testid="ticket-stats">
            {[
              { label: 'Total', value: stats.total || 0, color: 'purple' },
              { label: 'Pending', value: stats.pending || 0, color: 'gray' },
              { label: 'In Progress', value: stats.in_progress || 0, color: 'amber' },
              { label: 'Resolved', value: stats.resolved || 0, color: 'emerald' },
              { label: 'Resolution Rate', value: `${stats.resolution_rate || 0}%`, color: 'blue' },
            ].map(s => (
              <div key={s.label} className="bg-white/[0.03] border border-white/10 rounded-xl p-3 text-center">
                <p className="text-xl font-bold text-white">{s.value}</p>
                <p className="text-[10px] text-gray-500">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* List View */}
        {view === 'list' && (
          <>
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search tickets..." className="bg-gray-900/50 border-white/10 text-white pl-10 h-9" data-testid="ticket-search" />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="bg-gray-900/50 border-white/10 text-white w-36 h-9" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-white/10">
                  <SelectItem value="all" className="text-white">All Status</SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([k, v]) => <SelectItem key={k} value={k} className="text-white">{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="bg-gray-900/50 border-white/10 text-white w-36 h-9" data-testid="priority-filter">
                  <SelectValue placeholder="Priority" />
                </SelectTrigger>
                <SelectContent className="bg-gray-900 border-white/10">
                  <SelectItem value="all" className="text-white">All Priority</SelectItem>
                  {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <SelectItem key={k} value={k} className="text-white">{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2" data-testid="tickets-list">
              {loading ? (
                <div className="text-center py-16 text-gray-400">Loading tickets...</div>
              ) : tickets.length === 0 ? (
                <Card className="bg-gray-900/50 border-white/10">
                  <CardContent className="py-16 text-center">
                    <AlertTriangle className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-xl font-semibold text-white mb-2" data-testid="no-tickets-msg">No tickets yet</h3>
                    <p className="text-gray-400 mb-4">Tickets are auto-generated from calls or created manually.</p>
                    <Button onClick={() => setShowCreateModal(true)} className="bg-purple-600 hover:bg-purple-700 text-white">
                      <Plus className="w-4 h-4 mr-2" /> Create Ticket
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                tickets.map(t => {
                  const sc = STATUS_CONFIG[t.status] || STATUS_CONFIG.pending;
                  const pc = PRIORITY_CONFIG[t.priority] || PRIORITY_CONFIG.medium;
                  return (
                    <div key={t.id} onClick={() => openDetail(t)} className="bg-gray-900/50 border border-white/10 hover:border-purple-500/30 rounded-lg p-4 cursor-pointer transition-all flex items-center gap-4" data-testid={`ticket-row-${t.id}`}>
                      <div className={`w-2 h-10 rounded-full flex-shrink-0 ${t.priority === 'high' ? 'bg-red-500' : t.priority === 'medium' ? 'bg-amber-500' : 'bg-gray-500'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <p className="text-white font-medium text-sm truncate">{t.title}</p>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          {t.contact_name && <span>{t.contact_name}</span>}
                          {t.source === 'call' && <span className="flex items-center gap-1"><PhoneCall className="w-3 h-3" /> From call</span>}
                          {t.source === 'chat' && <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" /> From chat</span>}
                          <span>{new Date(t.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {t.contact_phone && (
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/crm?phone=${encodeURIComponent(t.contact_phone)}`); }}
                            title="View contact in CRM"
                            className="p-1.5 rounded-md text-gray-500 hover:text-blue-400 hover:bg-blue-500/10 transition-colors"
                            data-testid={`ticket-crm-link-${t.id}`}
                          >
                            <ExternalLink className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${sc.cls}`}>{sc.label}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${pc.cls}`}>{pc.label}</span>
                        <ChevronRight className="w-4 h-4 text-gray-600" />
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </>
        )}

        {/* Detail View */}
        {view === 'detail' && selectedTicket && (
          <div className="max-w-3xl mx-auto space-y-6" data-testid="ticket-detail">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-bold text-white mb-1" data-testid="ticket-title">{selectedTicket.title}</h2>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  {selectedTicket.source === 'call' && <span className="flex items-center gap-1"><PhoneCall className="w-3 h-3" /> From call</span>}
                  {selectedTicket.source === 'chat' && <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" /> From chat</span>}
                  {selectedTicket.contact_name && <span>{selectedTicket.contact_name}</span>}
                  <span>{new Date(selectedTicket.created_at).toLocaleDateString()}</span>
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={() => handleDelete(selectedTicket.id)} className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="delete-ticket-btn">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-4">
                {selectedTicket.description && (
                  <Card className="bg-gray-900/50 border-white/10">
                    <CardHeader><CardTitle className="text-white text-sm">Description</CardTitle></CardHeader>
                    <CardContent><p className="text-gray-300 text-sm whitespace-pre-line">{selectedTicket.description}</p></CardContent>
                  </Card>
                )}

                {/* Notes */}
                <Card className="bg-gray-900/50 border-white/10">
                  <CardHeader><CardTitle className="text-white text-sm">Notes ({(selectedTicket.notes || []).length})</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    {(selectedTicket.notes || []).map((n, i) => (
                      <div key={i} className="p-3 bg-black/20 rounded-lg">
                        <p className="text-gray-300 text-sm">{n.text}</p>
                        <p className="text-gray-600 text-xs mt-1">{new Date(n.created_at).toLocaleString()}</p>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <Input value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Add a note..." className="bg-black/30 border-white/10 text-white text-sm h-9 flex-1" data-testid="add-note-input" onKeyDown={e => e.key === 'Enter' && handleAddNote(selectedTicket.id)} />
                      <Button size="sm" onClick={() => handleAddNote(selectedTicket.id)} className="bg-purple-600 hover:bg-purple-700 text-white h-9" data-testid="add-note-btn">
                        <MessageSquare className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Sidebar */}
              <div>
                <Card className="bg-gray-900/50 border-white/10 sticky top-24">
                  <CardHeader><CardTitle className="text-white text-sm">Properties</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="text-gray-500 text-xs">Status</Label>
                      <Select value={selectedTicket.status} onValueChange={v => handleStatusChange(selectedTicket.id, v)}>
                        <SelectTrigger className="bg-black/30 border-white/10 text-white h-8 text-sm mt-1" data-testid="ticket-status-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-gray-900 border-white/10">
                          {Object.entries(STATUS_CONFIG).map(([k, v]) => <SelectItem key={k} value={k} className="text-white">{v.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-gray-500 text-xs">Priority</Label>
                      <Select value={selectedTicket.priority} onValueChange={v => handlePriorityChange(selectedTicket.id, v)}>
                        <SelectTrigger className="bg-black/30 border-white/10 text-white h-8 text-sm mt-1" data-testid="ticket-priority-select"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-gray-900 border-white/10">
                          {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <SelectItem key={k} value={k} className="text-white">{v.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    {selectedTicket.contact_phone && (
                      <div>
                        <Label className="text-gray-500 text-xs">Contact Phone</Label>
                        <p className="text-white text-sm font-mono mt-1">{selectedTicket.contact_phone}</p>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/crm?phone=${encodeURIComponent(selectedTicket.contact_phone)}`)}
                          className="mt-2 w-full border-blue-500/30 text-blue-400 hover:bg-blue-500/10 text-xs h-8"
                          data-testid="ticket-view-in-crm-btn"
                        >
                          <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> View in CRM
                        </Button>
                      </div>
                    )}
                    {(selectedTicket.tags || []).length > 0 && (
                      <div>
                        <Label className="text-gray-500 text-xs">Tags</Label>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {selectedTicket.tags.map((t, i) => (
                            <span key={i} className="text-[10px] px-2 py-0.5 bg-purple-500/10 text-purple-400 rounded-full">{t}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {selectedTicket.resolved_at && (
                      <div>
                        <Label className="text-gray-500 text-xs">Resolved At</Label>
                        <p className="text-emerald-400 text-xs mt-1">{new Date(selectedTicket.resolved_at).toLocaleString()}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-6" data-testid="create-ticket-modal">
          <Card className="bg-gray-900 border-white/10 w-full max-w-md">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white">New Ticket</CardTitle>
                <Button size="sm" variant="ghost" onClick={() => setShowCreateModal(false)} className="text-gray-400 h-8 w-8 p-0"><X className="w-4 h-4" /></Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label className="text-gray-400 text-xs">Title *</Label>
                <Input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} placeholder="Ticket title" className="bg-black/30 border-white/10 text-white" data-testid="ticket-title-input" />
              </div>
              <div>
                <Label className="text-gray-400 text-xs">Description</Label>
                <Textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} placeholder="Details..." className="bg-black/30 border-white/10 text-white min-h-[80px]" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-gray-400 text-xs">Priority</Label>
                  <Select value={form.priority} onValueChange={v => setForm(f => ({...f, priority: v}))}>
                    <SelectTrigger className="bg-black/30 border-white/10 text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-gray-900 border-white/10">
                      {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <SelectItem key={k} value={k} className="text-white">{v.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-400 text-xs">Contact Name</Label>
                  <Input value={form.contact_name} onChange={e => setForm(f => ({...f, contact_name: e.target.value}))} className="bg-black/30 border-white/10 text-white" />
                </div>
              </div>
              <Button onClick={handleCreate} className="w-full bg-purple-600 hover:bg-purple-700 text-white" data-testid="save-ticket-btn">Create Ticket</Button>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Tickets;
